import { AnimatePresence, motion } from 'framer-motion'
import {
  AlertTriangle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RotateCcw,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { scorePct } from '@/features/entity-resolution/cluster-score'
import {
  AUDIT_PAGE_SIZE,
  useAuditLog,
  useAuditRecord,
  useRevertMerge,
} from '@/features/entity-resolution/hooks/useAuditLog'
import {
  mergeReasonBadge,
  mergeReasonSentence,
  mergeReasonText,
} from '@/features/entity-resolution/merge-reason'
import { useDebounced } from '@/hooks/use-debounced'
import type { AuditSummary } from '@/services/api/entity-resolution-audit'
import { cn } from '@/utils/cn'
import {
  AuditEntityComparison,
  AuditMergeExplanation,
  AuditPropertySnapshot,
} from './AuditMergeExplanation'

/** Translation namespace shared by the table, the drawer and the empty states. */
const AUDIT = 'cases.investigate.entityResolution.audit'

function formatWhen(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function shortId(id: string | null | undefined): string {
  if (!id) return '—'
  return id.split(':').pop() ?? id
}

// The seven-column table needs a desktop width. Below lg the header row is gone
// and each record stacks (see AuditRow): who merged into whom on the first line,
// then when, type and status; the rest is one tap away in the drawer.
const GRID =
  'items-center lg:grid lg:grid-cols-[160px_116px_minmax(0,1.7fr)_minmax(0,1fr)_60px_56px_84px] lg:gap-3'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 items-start gap-x-3 gap-y-0.5 py-1 text-xs md:grid-cols-[136px_1fr] md:gap-3">
      <span className="min-w-0 break-words text-2xs uppercase leading-tight tracking-wider text-muted-foreground/60">
        {label}
      </span>
      <span className="min-w-0 break-words text-foreground">{children}</span>
    </div>
  )
}

export function AuditDetailDrawer({
  caseId,
  auditId,
  onClose,
  onOpenAudit,
}: {
  caseId: string
  auditId: string
  onClose: () => void
  onOpenAudit: (id: string) => void
}) {
  const { t } = useTranslation()
  const { data: record, isLoading, isError } = useAuditRecord(caseId, auditId)
  const revert = useRevertMerge(caseId)
  const [confirming, setConfirming] = useState(false)
  const reverted = Boolean(record?.reverted_at)

  return (
    <>
      <motion.div
        className="fixed inset-0 z-40 bg-[var(--overlay-backdrop)]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={t(`${AUDIT}.drawer.title`)}
        className="fixed inset-y-0 right-0 z-50 flex w-[760px] max-w-[96vw] flex-col border-l border-border bg-card"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 320, damping: 34 }}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border/60 px-4 py-3 md:px-5 md:py-4">
          <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground/70">
            {t(`${AUDIT}.drawer.title`)}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="flex size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted md:size-7"
          >
            <X className="size-4" />
          </button>
        </div>

        {isError ? (
          <p role="alert" className="p-5 text-sm text-muted-foreground">
            {t(`${AUDIT}.drawer.loadError`)}
          </p>
        ) : isLoading || !record ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 md:p-5">
            <AuditEntityComparison record={record} onOpenAudit={onOpenAudit} />
            <AuditMergeExplanation record={record} />
            <AuditPropertySnapshot record={record} />

            {/* relationships */}
            <p className="mt-5 mb-2 text-2xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t(`${AUDIT}.drawer.relationships`, {
                count: record.merged_node_relationships?.length ?? 0,
              })}
            </p>
            <div className="flex flex-col gap-1.5">
              {(record.merged_node_relationships ?? []).map((rel, i) => (
                <div
                  key={`${rel.type}-${rel.other_node_id}-${i}`}
                  className="flex items-center gap-2 rounded-lg border border-border/50 bg-card px-2.5 py-2 text-xs max-md:flex-wrap"
                >
                  <Badge variant={rel.direction === 'OUT' ? 'info' : 'muted'} className="shrink-0">
                    {rel.direction === 'OUT' ? '→' : '←'} {rel.type}
                  </Badge>
                  <span className="min-w-0 flex-1 break-words text-muted-foreground max-md:basis-full md:truncate">
                    {(rel.other_node_labels ?? []).join(', ') || t(`${AUDIT}.drawer.node`)} ·{' '}
                    <span className="font-mono text-2xs">{shortId(rel.other_node_id)}</span>
                  </span>
                </div>
              ))}
              {(record.merged_node_relationships?.length ?? 0) === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t(`${AUDIT}.drawer.noRelationships`)}
                </p>
              )}
            </div>
            <div className="mt-5 rounded-xl border border-border/60 p-3">
              <Field label={t(`${AUDIT}.columns.when`)}>{formatWhen(record.created_at)}</Field>
              <Field label={t(`${AUDIT}.columns.type`)}>
                {/* The reason is a sentence: in the phone-width drawer it wraps
                    instead of running out of the panel. */}
                <Badge
                  variant={mergeReasonBadge(record.merge_reason).tone}
                  className="max-md:h-auto max-md:rounded-xl max-md:py-1 max-md:whitespace-normal"
                >
                  {mergeReasonSentence(t, record.merge_reason)}
                </Badge>
              </Field>
              <Field label={t(`${AUDIT}.drawer.triggeredBy`)}>{record.triggered_by}</Field>
              <Field label={t(`${AUDIT}.drawer.similarity`)}>
                {scorePct(record.similarity_score)}
              </Field>
              <Field label={t(`${AUDIT}.drawer.cluster`)}>{shortId(record.cluster_id)}</Field>
              <Field label={t(`${AUDIT}.drawer.mergedId`)}>
                <span className="font-mono text-2xs">{record.merged_node_id}</span>
              </Field>
              <Field label={t(`${AUDIT}.drawer.labels`)}>
                <span className="flex flex-wrap gap-1">
                  {record.merged_node_labels.map(l => (
                    <Badge key={l} variant="muted">
                      {l}
                    </Badge>
                  ))}
                </span>
              </Field>
            </div>
          </div>
        )}

        {/* revert footer */}
        {record && (
          <div className="shrink-0 border-t border-border/60 p-4">
            {reverted ? (
              <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground">
                <RotateCcw className="size-3.5" />
                {record.reverted_by
                  ? t(`${AUDIT}.drawer.revertedAtBy`, {
                      when: formatWhen(record.reverted_at),
                      by: record.reverted_by,
                    })
                  : t(`${AUDIT}.drawer.revertedAt`, { when: formatWhen(record.reverted_at) })}
              </div>
            ) : confirming ? (
              <div className="flex flex-col gap-2.5">
                <p className="flex items-start gap-1.5 text-2xs text-muted-foreground">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
                  {t(`${AUDIT}.drawer.revertWarning`)}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    className="flex-1"
                    disabled={revert.isPending}
                    onClick={() => revert.mutate(record.id, { onSuccess: onClose })}
                  >
                    {revert.isPending ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                    {t(`${AUDIT}.drawer.confirmRevert`)}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={() => setConfirming(true)}
              >
                <RotateCcw />
                {t(`${AUDIT}.drawer.revert`)}
              </Button>
            )}
          </div>
        )}
      </motion.div>
    </>
  )
}

function AuditRow({ record, onOpen }: { record: AuditSummary; onOpen: () => void }) {
  const { t } = useTranslation()
  // Short label for the dense table; the full reason shows in the detail drawer.
  const { badge, tone } = mergeReasonBadge(record.merge_reason)
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        GRID,
        'flex w-full flex-wrap gap-x-2 gap-y-1.5 border-b border-border/40 px-4 py-2.5 text-left text-sm hover:bg-muted/30'
      )}
    >
      <span className="text-xs text-muted-foreground">{formatWhen(record.created_at)}</span>
      <Badge variant={tone} className="w-fit">
        {badge ? t(`${AUDIT}.reasonBadges.${badge}`) : mergeReasonText(record.merge_reason)}
      </Badge>
      <span className="order-first flex min-w-0 basis-full items-center gap-1.5 max-md:flex-wrap max-md:gap-y-0 lg:order-none">
        <span className="truncate font-medium text-foreground">
          {record.merged_node_name ?? shortId(record.merged_node_id)}
        </span>
        <ArrowRight className="size-3 shrink-0 text-muted-foreground" />
        <span className="truncate text-primary">
          {record.canonical_node_key ?? shortId(record.canonical_node_id)}
        </span>
      </span>
      <span className="truncate text-xs text-muted-foreground max-lg:hidden">
        {record.triggered_by}
      </span>
      <span className="text-xs tabular-nums text-muted-foreground max-lg:hidden">
        {scorePct(record.similarity_score)}
      </span>
      <span className="text-xs tabular-nums text-muted-foreground max-lg:hidden">
        {record.relationship_count}
      </span>
      {record.reverted_at ? (
        <Badge variant="muted" className="w-fit max-lg:ml-auto">
          {t(`${AUDIT}.status.reverted`)}
        </Badge>
      ) : (
        <Badge variant="success" className="w-fit max-lg:ml-auto">
          {t(`${AUDIT}.status.active`)}
        </Badge>
      )}
    </button>
  )
}

export function AuditLogPanel({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [openId, setOpenId] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')

  // Debounce the raw input so each keystroke doesn't fire a request, and reset
  // to the first page whenever the active search term changes.
  const search = useDebounced(searchInput.trim(), 250)
  useEffect(() => {
    setPage(0)
  }, [search])

  const { data, isLoading, isError, isPlaceholderData, isFetching } = useAuditLog(
    caseId,
    page,
    search
  )

  const records = data?.records ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE))
  const start = page * AUDIT_PAGE_SIZE
  const emptyState = `${AUDIT}.empty.${search ? 'noMatches' : 'noRecords'}`

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 bg-background p-4 md:p-5">
      <SearchInput
        value={searchInput}
        onValueChange={setSearchInput}
        loading={isFetching && Boolean(search)}
        placeholder={t(`${AUDIT}.search.placeholder`)}
        aria-label={t(`${AUDIT}.search.label`)}
        className="max-md:text-ellipsis max-md:placeholder:text-sm"
        onKeyDown={e => {
          if (e.key === 'Escape' && searchInput) {
            e.preventDefault()
            setSearchInput('')
          }
        }}
        trailing={
          search && !isFetching ? (
            <span className="shrink-0 text-2xs tabular-nums text-muted-foreground/70">
              {t(`${AUDIT}.search.matchCount`, { count: total })}
            </span>
          ) : null
        }
      />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card">
        {/* header */}
        <div
          className={cn(
            GRID,
            'shrink-0 border-b border-border/60 px-4 py-2.5 text-2xs font-medium uppercase tracking-wider text-muted-foreground/70 max-lg:hidden'
          )}
        >
          <span>{t(`${AUDIT}.columns.when`)}</span>
          <span>{t(`${AUDIT}.columns.type`)}</span>
          <span>{t(`${AUDIT}.columns.mergedToCanonical`)}</span>
          <span>{t(`${AUDIT}.columns.by`)}</span>
          <span>{t(`${AUDIT}.columns.score`)}</span>
          <span>{t(`${AUDIT}.columns.edges`)}</span>
          <span>{t(`${AUDIT}.columns.status`)}</span>
        </div>

        {/* rows */}
        <div className={cn('min-h-0 flex-1 overflow-y-auto', isPlaceholderData && 'opacity-60')}>
          {isLoading ? (
            <div className="flex flex-col">
              {Array.from({ length: 12 }, (_, i) => `sk-${i}`).map(k => (
                <div key={k} className="border-b border-border/40 px-4 py-3">
                  <div className="h-4 animate-pulse rounded bg-muted/40" />
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t(`${AUDIT}.loadError`)}
            </div>
          ) : records.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
              <p className="text-sm font-medium text-foreground">{t(`${emptyState}.title`)}</p>
              <p className="max-w-sm text-xs text-muted-foreground">{t(`${emptyState}.hint`)}</p>
            </div>
          ) : (
            records.map(r => <AuditRow key={r.id} record={r} onOpen={() => setOpenId(r.id)} />)
          )}
        </div>

        {/* pagination */}
        <div className="flex shrink-0 items-center justify-between border-t border-border/60 px-4 py-2.5">
          <span className="text-2xs text-muted-foreground">
            {total === 0
              ? t(`${AUDIT}.pagination.noRecords`)
              : t(`${AUDIT}.pagination.range`, {
                  from: start + 1,
                  to: Math.min(start + AUDIT_PAGE_SIZE, total),
                  total,
                })}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="icon-xs"
              variant="ghost"
              className="max-md:size-9"
              disabled={page === 0}
              onClick={() => setPage(p => Math.max(0, p - 1))}
              aria-label={t(`${AUDIT}.pagination.previousPage`)}
            >
              <ChevronLeft />
            </Button>
            <span className="text-2xs tabular-nums text-muted-foreground">
              {page + 1} / {pageCount}
            </span>
            <Button
              size="icon-xs"
              variant="ghost"
              className="max-md:size-9"
              disabled={page >= pageCount - 1}
              onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
              aria-label={t(`${AUDIT}.pagination.nextPage`)}
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {openId && (
          <AuditDetailDrawer
            key={openId}
            caseId={caseId}
            auditId={openId}
            onClose={() => setOpenId(null)}
            onOpenAudit={setOpenId}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
